/**
 * 內視鏡影像來源（WebRTC Media Capture）
 *
 * 從 KKASR 的 static/js/camera.js 搬過來，行為一致，差別只有「不再自己找
 * DOM」——原檔直接 getElementById 抓 select / video / status 三個節點，那在
 * Vue 裡會變成元件與模組互相偷改對方的 DOM。這裡改成建構時傳入 <video>，
 * 其餘（裝置清單、狀態訊息、作用中名稱）一律用回呼往外送，由元件自己渲染。
 *
 * 與 ASR 主線完全無關：鏡頭掛掉不影響收音與 LLM（原檔也是這個設計）。
 */

export class CameraController {
  /**
   * @param {HTMLVideoElement} videoEl 預覽用的 <video>
   */
  constructor(videoEl) {
    this.videoEl = videoEl
    this.stream = null

    this._onStatus = null
    this._onDevices = null
    this._onActiveName = null

    this._handleDeviceChange = this._handleDeviceChange.bind(this)
  }

  onStatus(cb) {
    this._onStatus = cb
  }

  onDevices(cb) {
    this._onDevices = cb
  }

  onActiveName(cb) {
    this._onActiveName = cb
  }

  static isSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  }

  _emitStatus(message, isError = false) {
    if (this._onStatus) this._onStatus({ message, isError })
  }

  _emitActiveName(name) {
    if (this._onActiveName) this._onActiveName(name || '')
  }

  /**
   * 錯誤訊息刻意逐條寫死中文：醫院現場出問題時，畫面上要直接講「該怎麼辦」，
   * 不能只吐 NotReadableError 讓使用者去查。
   */
  _emitError(err) {
    let message
    switch (err && err.name) {
      case 'NotAllowedError':
        message = '攝影機權限被拒絕，請在網址列左側允許攝影機後重新整理頁面'
        break
      case 'NotFoundError':
        message = '找不到攝影機裝置，請確認內視鏡擷取卡或鏡頭已接上'
        break
      case 'NotReadableError':
        message = '攝影機被其他程式占用（擷取卡可能已被別的軟體開啟）'
        break
      default:
        message = `攝影機錯誤: ${err && err.name}: ${err && err.message}`
    }
    this._emitStatus(message, true)
  }

  stop() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
    if (this.videoEl) this.videoEl.srcObject = null
  }

  /**
   * 開啟指定裝置的畫面。
   *
   * 先停舊 stream 再開新的：UVC 擷取卡常是獨占存取，兩條 stream 同時開會失敗。
   */
  async start(deviceId) {
    this.stop()

    const constraints = {
      audio: false,
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
    }

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints)
    } catch (err) {
      if (err && err.name === 'OverconstrainedError' && deviceId) {
        // 指定裝置剛被拔掉 → 降級用任一鏡頭，總比整個黑掉好
        return this.start(null)
      }
      this._emitError(err)
      this._emitActiveName('')
      return null
    }

    this.stream = stream
    if (this.videoEl) this.videoEl.srcObject = stream
    this._emitStatus('')

    const track = stream.getVideoTracks()[0]
    this._emitActiveName(track && track.label)
    return track ? track.getSettings().deviceId || null : null
  }

  async listDevices() {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const videoInputs = devices
      .filter((d) => d.kind === 'videoinput')
      .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }))
    if (this._onDevices) this._onDevices(videoInputs)
    return videoInputs
  }

  async _handleDeviceChange() {
    const prevId = this.currentDeviceId
    const list = await this.listDevices()

    if (list.length === 0) {
      this.stop()
      this._emitStatus('未偵測到攝影機裝置', true)
      this._emitActiveName('')
      return
    }

    // 現用裝置還在就不動它——新插入的裝置不該把畫面搶走
    if (list.some((d) => d.deviceId === prevId)) return

    this.currentDeviceId = await this.start(list[0].deviceId)
  }

  /**
   * 初始化：先 getUserMedia 拿授權，enumerateDevices 才拿得到裝置名稱
   * （未授權前 label 一律是空字串，選單會變成一排 "Camera 1/2/3"）。
   */
  async init() {
    if (!CameraController.isSupported()) {
      const hint = window.isSecureContext
        ? '此瀏覽器不支援 Media Capture API'
        : '目前透過 http://IP 存取，瀏覽器停用攝影機 / 麥克風 API。' +
          '請改用 http://localhost 開啟，或在 chrome://flags/#unsafely-treat-insecure-origin-as-secure 加入本站網址。'
      this._emitStatus(hint, true)
      this._emitActiveName('')
      return false
    }

    this.currentDeviceId = await this.start(null)
    await this.listDevices()
    navigator.mediaDevices.addEventListener('devicechange', this._handleDeviceChange)
    return true
  }

  async select(deviceId) {
    this.currentDeviceId = await this.start(deviceId)
  }

  destroy() {
    if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this._handleDeviceChange)
    }
    this.stop()
    this._onStatus = null
    this._onDevices = null
    this._onActiveName = null
  }
}
